import type { CompanyTeamMember, TeamRole } from '@indutrust/shared-model';

const ALL_TEAM_ROLES: readonly TeamRole[] = ['owner', 'manager', 'viewer'];

function countOwners(members: CompanyTeamMember[]): number {
  return members.filter((entry) => entry.role === 'owner').length;
}

function isDemotion(currentRole: TeamRole, newRole: TeamRole): boolean {
  const rank: Record<TeamRole, number> = {
    manager: 2,
    owner: 3,
    viewer: 1,
  };
  return rank[newRole] < rank[currentRole];
}

/** Whether the actor may mutate the team surface at all. */
export function canManageTeam(actor: TeamRole): boolean {
  return actor !== 'viewer';
}

/**
 * Roles the actor may assign when inviting or promoting teammates (R5
 * same-level cap). Owner may grant any role; Manager may grant Manager
 * or Viewer; Viewer may grant Viewer only (but cannot manage the team).
 */
export function getGrantableTeamRoles(actor: TeamRole): TeamRole[] {
  switch (actor) {
    case 'owner':
      return [...ALL_TEAM_ROLES];
    case 'manager':
      return ['manager', 'viewer'];
    case 'viewer':
      return ['viewer'];
  }
}

/** Whether the actor may invite at the requested role. */
export function canInviteAtRole(actor: TeamRole, role: TeamRole): boolean {
  return canManageTeam(actor) && getGrantableTeamRoles(actor).includes(role);
}

/**
 * Whether the actor may change `member` to `newRole`. Enforces the
 * same-level cap, Manager cannot touch Owners, and demotions are
 * Owner-only.
 */
export function canPromoteMemberTo(
  actor: TeamRole,
  member: CompanyTeamMember,
  newRole: TeamRole,
  members: CompanyTeamMember[]
): boolean {
  if (!canManageTeam(actor)) {
    return false;
  }
  if (!getGrantableTeamRoles(actor).includes(newRole)) {
    return false;
  }
  if (actor === 'manager' && member.role === 'owner') {
    return false;
  }
  if (
    member.role === 'owner' &&
    newRole !== 'owner' &&
    countOwners(members) === 1
  ) {
    return false;
  }
  if (member.role !== newRole && isDemotion(member.role, newRole)) {
    return actor === 'owner';
  }
  return true;
}

/**
 * Whether the actor may remove `member`. Viewers cannot remove anyone;
 * Managers cannot remove Owners; the last Owner cannot be removed.
 */
export function canRemoveMember(
  actor: TeamRole,
  member: CompanyTeamMember,
  members: CompanyTeamMember[]
): boolean {
  if (!canManageTeam(actor)) {
    return false;
  }
  if (actor === 'manager' && member.role === 'owner') {
    return false;
  }
  if (member.role === 'owner' && countOwners(members) === 1) {
    return false;
  }
  return true;
}

/** Whether the actor may open role edit for `member`. */
export function canEditMemberRole(
  actor: TeamRole,
  member: CompanyTeamMember,
  members: CompanyTeamMember[]
): boolean {
  if (!canManageTeam(actor)) {
    return false;
  }
  return getGrantableTeamRoles(actor).some(
    (role) =>
      role !== member.role && canPromoteMemberTo(actor, member, role, members)
  );
}

/** Role options the actor may assign to `member`. */
export function getAssignableRolesForMember(
  actor: TeamRole,
  member: CompanyTeamMember,
  members: CompanyTeamMember[]
): TeamRole[] {
  return getGrantableTeamRoles(actor).filter(
    (role) =>
      role !== member.role && canPromoteMemberTo(actor, member, role, members)
  );
}
